import { StyleSheet, css } from 'aphrodite'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DISPATCH_TEMPLATE_CONFIG,
  type ProjectTemplateRecipientType,
} from '../../config/templateConfig'
import { useUser } from '../../contexts/UserContext'
import { useWorkOrderContext } from '../../contexts/WorkOrderContext'
import { useAutoSave } from '../../hooks/useAutoSave'
import { projectService } from '../../services/projectService'
import { workOrderService } from '../../services/workOrderService'
import type { Attachment, Project } from '../../types/project'
import type { User } from '../../types/user'
import { StakeholderType, UserType } from '../../types/user'
import type { WorkOrder } from '../../types/workorder'
import { WorkOrderStatus } from '../../types/workorder'
import DashboardAddModal from '../dashboard/DashboardAddModal'
import ContactInfoStep from './ContactInfoStep'
import JobDetailsStep from './JobDetailsStep'
import PaymentInfoStep from './PaymentInfoStep'
import TemplateContactInfoStep from './TemplateContactInfoStep'
import WorkOrderConfirmation from './WorkOrderConfirmation'

interface AddWorkOrderModalProps {
  onClose: () => void
  project?: Project
  templateRecipientType?: ProjectTemplateRecipientType
}

export const WORKORDER_DRAFT_KEY = 'daydaco_workorder_draft'

const AddWorkOrderModal: React.FC<AddWorkOrderModalProps> = ({
  onClose,
  project,
  templateRecipientType,
}) => {
  const { createWorkOrder, refreshWorkOrders } = useWorkOrderContext()
  const { user: currentUser } = useUser()
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [fullProject, setFullProject] = useState<Project | null>(null)
  const [isStepValid, setIsStepValid] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const [dispatchLabel, setDispatchLabel] = useState<string>('')
  const hasGeneratedLabel = useRef(false)
  // Track the backend draft ID for auto-save updates
  const [backendDraftId, setBackendDraftId] = useState<number | null>(null)

  // Attachment state
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([])

  // Clear draft - now handled by useAutoSave hook but kept for compatibility
  const clearDraftManual = () => {
    try {
      localStorage.removeItem(WORKORDER_DRAFT_KEY)
    } catch (error) {
      console.error('Failed to clear workorder draft:', error)
    }
  }

  // Reset validation state when step changes
  useEffect(() => {
    // Reset validation when navigating to a new step
    void step // Reference step to satisfy exhaustive-deps
    setIsStepValid(false)
    setShowErrors(false)
  }, [step])

  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsStepValid(isValid)
  }, [])

  // Fetch full project data if we only have an ID
  useEffect(() => {
    const fetchFullProject = async () => {
      if (
        project?.id &&
        (!project.created_by || typeof project.created_by === 'number')
      ) {
        try {
          const fullProjectData = await projectService.getProject(project.id)
          setFullProject(fullProjectData)
        } catch (error) {
          console.error('Failed to fetch full project data:', error)
        }
      } else {
        setFullProject(project || null)
      }
    }

    fetchFullProject()
  }, [project])

  // Generate dispatch label on mount (or use existing from draft)
  useEffect(() => {
    const generateDispatchLabel = async () => {
      if (hasGeneratedLabel.current) return
      hasGeneratedLabel.current = true

      // Check if draft already has a label (using ref to get initial value)
      const savedDraft = localStorage.getItem(WORKORDER_DRAFT_KEY)
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft)
          if (parsed.label) {
            setDispatchLabel(parsed.label)
            return
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      try {
        const { label } = await workOrderService.generateLabel()
        setDispatchLabel(label)
        setFormData((prev) => ({ ...prev, label }))
      } catch (error) {
        console.error('Failed to generate dispatch label:', error)
      }
    }

    generateDispatchLabel()
  }, [])

  // Initialize form data - check localStorage for draft
  const getInitialFormData = (): Partial<WorkOrder> => {
    // Try to load draft from localStorage
    try {
      const savedDraft = localStorage.getItem(WORKORDER_DRAFT_KEY)
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft)
        // Only use draft if it's for the same project (or no project specified)
        if (
          !project?.id ||
          parsed.project === Number(project.id) ||
          !parsed.project
        ) {
          console.log('Loaded workorder draft from localStorage')
          // Ensure project ID is set correctly
          return {
            ...parsed,
            project: project?.id ? Number(project.id) : parsed.project,
            created_by: project?.created_by || parsed.created_by,
          }
        }
      }
    } catch (error) {
      console.error('Failed to load workorder draft:', error)
    }

    return {
      project: project?.id ? Number(project.id) : undefined,
      created_by: project?.created_by || undefined,
      trucks_number: '',
      truck_types: [],
      material: '',
      volume: '0',
      pay_rate: '',
      pickup_address: '',
      dropoff_address: '',
      starting_date: '',
      completion_date: '',
      template_recipient_type: templateRecipientType,
    }
  }

  const [formData, setFormData] =
    useState<Partial<WorkOrder>>(getInitialFormData)

  // Backend draft ID ref for use in callbacks (avoids stale closure issues)
  const backendDraftIdRef = useRef(backendDraftId)
  useEffect(() => {
    backendDraftIdRef.current = backendDraftId
  }, [backendDraftId])

  // Dispatch label ref for use in callbacks
  const dispatchLabelRef = useRef(dispatchLabel)
  useEffect(() => {
    dispatchLabelRef.current = dispatchLabel
  }, [dispatchLabel])

  // Auto-save to backend callback
  const handleAutoSaveDraft = useCallback(
    async (data: Partial<WorkOrder>) => {
      // Don't save to backend if no label yet (still loading)
      if (!dispatchLabelRef.current) {
        console.log('Skipping backend auto-save: no label yet')
        return
      }

      // Get project ID - always use from props since dispatches are created from project context
      const projectId = project?.id ? Number(project.id) : data.project

      if (!projectId) {
        console.log('Skipping backend auto-save: no project ID')
        return
      }

      try {
        // Build work order data for backend, only including fields with values
        const workOrderData: Record<string, unknown> = {
          status: WorkOrderStatus.DRAFT,
          project: projectId, // Pass as 'project' so createWorkOrder can extract it
          ...(dispatchLabelRef.current
            ? { label: dispatchLabelRef.current }
            : {}),
          ...(templateRecipientType
            ? { template_recipient_type: templateRecipientType }
            : {}),
          ...(data.trucks_number ? { trucks_number: data.trucks_number } : {}),
          ...(data.truck_types?.length
            ? { truck_types: data.truck_types }
            : {}),
          ...(data.material ? { material: data.material } : {}),
          ...(data.volume && data.volume !== '0'
            ? { volume: data.volume }
            : {}),
          ...(data.pay_rate ? { pay_rate: data.pay_rate } : {}),
          ...(data.rate_type ? { rate_type: data.rate_type } : {}),
          ...(data.pickup_address
            ? { pickup_address: data.pickup_address }
            : {}),
          ...(data.dropoff_address
            ? { dropoff_address: data.dropoff_address }
            : {}),
          ...(data.starting_date ? { starting_date: data.starting_date } : {}),
          ...(data.completion_date
            ? { completion_date: data.completion_date }
            : {}),
          ...(data.notes ? { notes: data.notes } : {}),
          // Payment schedule fields
          ...(data.payment_type ? { payment_type: data.payment_type } : {}),
          ...(data.payment_schedule?.length
            ? { payment_schedule: data.payment_schedule }
            : {}),
          ...(data.total_scheduled_payment
            ? { total_scheduled_payment: data.total_scheduled_payment }
            : {}),
          ...(data.total_scheduled_fees
            ? { total_scheduled_fees: data.total_scheduled_fees }
            : {}),
          // Empty arrays for user assignments
          subhaulers: [],
          dispatchers: [],
        }

        let savedWorkOrder: WorkOrder
        const currentDraftId = backendDraftIdRef.current

        if (currentDraftId) {
          // Update existing draft
          console.log('Auto-save: updating existing draft:', currentDraftId)
          savedWorkOrder = await workOrderService.updateWorkOrder(
            String(currentDraftId),
            workOrderData as Partial<WorkOrder>,
          )
        } else {
          // Create new draft
          console.log('Auto-save: creating new draft')
          savedWorkOrder = await workOrderService.createWorkOrder(
            workOrderData as Omit<WorkOrder, 'id'>,
          )
          // Store the new draft ID for future updates
          if (savedWorkOrder.id) {
            setBackendDraftId(Number(savedWorkOrder.id))
          }
        }

        console.log('Auto-save to backend successful:', savedWorkOrder.id)
      } catch (error) {
        console.error('Auto-save to backend failed:', error)
        throw error // Let useAutoSave handle the error state
      }
    },
    [project?.id, templateRecipientType],
  )

  // Use debounced auto-save hook with backend save
  const {
    status: autoSaveStatus,
    lastSaved: autoSaveLastSaved,
    clearDraft,
  } = useAutoSave({
    storageKey: WORKORDER_DRAFT_KEY,
    data: formData,
    enabled: true,
    debounceMs: 3000, // 3 second debounce as per requirements
    onServerSave: handleAutoSaveDraft,
  })

  // Update formData when fullProject is loaded
  useEffect(() => {
    if (fullProject?.created_by) {
      setFormData((prev) => ({
        ...prev,
        created_by: fullProject.created_by,
      }))
    }
  }, [fullProject])

  const updateFormData = useCallback((data: Partial<WorkOrder>) => {
    setFormData((prev) => ({ ...prev, ...data }))
  }, [])

  // Handle immediate upload when we have a backend draft
  const handleAttachmentUpload = useCallback(
    async (files: File[]) => {
      if (backendDraftId) {
        // Upload immediately to existing draft
        const uploaded = await workOrderService.uploadAttachments(
          backendDraftId,
          files,
        )
        setAttachments((prev) => [...prev, ...uploaded])
      } else {
        // Queue for upload after draft creation
        setPendingAttachments((prev) => [...prev, ...files])
      }
    },
    [backendDraftId],
  )

  // Handle attachment deletion
  const handleAttachmentDelete = useCallback(
    async (attachmentId: number) => {
      if (backendDraftId) {
        await workOrderService.deleteAttachment(backendDraftId, attachmentId)
        setAttachments((prev) => prev.filter((a) => a.id !== attachmentId))
      }
    },
    [backendDraftId],
  )

  // Upload pending attachments when draft is created
  useEffect(() => {
    const uploadPending = async () => {
      if (backendDraftId && pendingAttachments.length > 0) {
        try {
          const uploaded = await workOrderService.uploadAttachments(
            backendDraftId,
            pendingAttachments,
          )
          setAttachments((prev) => [...prev, ...uploaded])
          setPendingAttachments([])
        } catch (error) {
          console.error('Failed to upload pending attachments:', error)
        }
      }
    }
    uploadPending()
  }, [backendDraftId, pendingAttachments])

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      // Get project ID from props or formData
      const projectId = project?.id ? Number(project.id) : formData.project

      // Build the final work order data
      const workOrderData: Record<string, unknown> = {
        ...(projectId ? { project: projectId } : {}),
        ...(dispatchLabel ? { label: dispatchLabel } : {}),
        ...(templateRecipientType
          ? { template_recipient_type: templateRecipientType }
          : {}),
        ...(formData.trucks_number
          ? { trucks_number: formData.trucks_number }
          : {}),
        ...(formData.truck_types?.length
          ? { truck_types: formData.truck_types }
          : {}),
        ...(formData.material ? { material: formData.material } : {}),
        ...(formData.volume && formData.volume !== '0'
          ? { volume: formData.volume }
          : {}),
        ...(formData.pay_rate ? { pay_rate: formData.pay_rate } : {}),
        ...(formData.rate_type ? { rate_type: formData.rate_type } : {}),
        ...(formData.pickup_address
          ? { pickup_address: formData.pickup_address }
          : {}),
        ...(formData.dropoff_address
          ? { dropoff_address: formData.dropoff_address }
          : {}),
        ...(formData.starting_date
          ? { starting_date: formData.starting_date }
          : {}),
        ...(formData.completion_date
          ? { completion_date: formData.completion_date }
          : {}),
        ...(formData.notes ? { notes: formData.notes } : {}),
        // Payment schedule fields
        ...(formData.payment_type
          ? { payment_type: formData.payment_type }
          : {}),
        ...(formData.payment_schedule?.length
          ? { payment_schedule: formData.payment_schedule }
          : {}),
        ...(formData.total_scheduled_payment
          ? { total_scheduled_payment: formData.total_scheduled_payment }
          : {}),
        ...(formData.total_scheduled_fees
          ? { total_scheduled_fees: formData.total_scheduled_fees }
          : {}),
        // User assignments
        subhaulers: formData.subhaulers?.map((user) => user.id) || [],
        dispatchers: formData.dispatchers?.map((user) => user.id) || [],
      }

      // Map contractor_dispatcher fields to dispatchers array for template dispatches
      if (
        templateRecipientType &&
        formData.contractor_dispatcher_id &&
        (!formData.dispatchers || formData.dispatchers.length === 0)
      ) {
        workOrderData.dispatchers = [formData.contractor_dispatcher_id]
      }

      // Check if we have an existing draft from auto-save
      if (backendDraftId) {
        // Update existing draft with final data, then send it
        console.log('Updating draft and sending:', backendDraftId)
        await workOrderService.updateWorkOrder(
          String(backendDraftId),
          workOrderData as Partial<WorkOrder>,
        )
        // Send the draft (transitions DRAFT -> PENDING and sends invitations)
        await workOrderService.sendDraft(backendDraftId)
      } else {
        // No existing draft - create and send directly
        await createWorkOrder(workOrderData as Omit<WorkOrder, 'id'>)
      }

      // Clear draft from localStorage after successful save
      clearDraft()
      onClose()
      navigate('/work-orders/pending')
      refreshWorkOrders()
    } catch (error) {
      console.error('Failed to create work order:', error)
      setSubmitError(
        error instanceof Error ? error.message : 'Failed to send dispatch',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveDraft = async () => {
    setIsSavingDraft(true)
    setSubmitError(null)
    try {
      // Get project ID from props or formData
      const projectId = project?.id ? Number(project.id) : formData.project

      // Build work order data, only including fields with actual values
      const workOrderData: Record<string, unknown> = {
        status: WorkOrderStatus.DRAFT,
        // Always include the project if available (from props or formData)
        ...(projectId ? { project: projectId } : {}),
        // Include label
        ...(dispatchLabel ? { label: dispatchLabel } : {}),
        ...(formData.label ? { label: formData.label } : {}),
        // Include template recipient type if specified
        ...(templateRecipientType
          ? { template_recipient_type: templateRecipientType }
          : {}),
        // Only include optional fields if they have actual values
        ...(formData.trucks_number
          ? { trucks_number: formData.trucks_number }
          : {}),
        ...(formData.truck_types?.length
          ? { truck_types: formData.truck_types }
          : {}),
        ...(formData.material ? { material: formData.material } : {}),
        ...(formData.volume && formData.volume !== '0'
          ? { volume: formData.volume }
          : {}),
        ...(formData.pay_rate ? { pay_rate: formData.pay_rate } : {}),
        ...(formData.rate_type ? { rate_type: formData.rate_type } : {}),
        ...(formData.pickup_address
          ? { pickup_address: formData.pickup_address }
          : {}),
        ...(formData.dropoff_address
          ? { dropoff_address: formData.dropoff_address }
          : {}),
        ...(formData.starting_date
          ? { starting_date: formData.starting_date }
          : {}),
        ...(formData.completion_date
          ? { completion_date: formData.completion_date }
          : {}),
        ...(formData.notes ? { notes: formData.notes } : {}),
        // Payment schedule fields
        ...(formData.payment_type
          ? { payment_type: formData.payment_type }
          : {}),
        ...(formData.payment_schedule?.length
          ? { payment_schedule: formData.payment_schedule }
          : {}),
        ...(formData.total_scheduled_payment
          ? { total_scheduled_payment: formData.total_scheduled_payment }
          : {}),
        ...(formData.total_scheduled_fees
          ? { total_scheduled_fees: formData.total_scheduled_fees }
          : {}),
        // Empty arrays for user assignments
        subhaulers: [],
        dispatchers: [],
      }

      let savedWorkOrder: WorkOrder

      // Check if we have an existing draft from auto-save
      if (backendDraftId) {
        // Update existing draft
        console.log('Updating existing draft:', backendDraftId)
        savedWorkOrder = await workOrderService.updateWorkOrder(
          String(backendDraftId),
          workOrderData as Partial<WorkOrder>,
        )
      } else {
        // Create new draft
        console.log('Creating new draft')
        savedWorkOrder = await workOrderService.createWorkOrder(
          workOrderData as Omit<WorkOrder, 'id'>,
        )
      }

      console.log('Draft saved successfully:', savedWorkOrder.id)

      // Clear draft from localStorage after successful save
      clearDraft()

      onClose()
      // Navigate to drafts tab
      navigate('/work-orders/drafts')
      refreshWorkOrders()
    } catch (error: unknown) {
      console.error('Failed to save draft:', error)
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to save draft. Please try again.'
      setSubmitError(errorMessage)
    } finally {
      setIsSavingDraft(false)
    }
  }

  // Show Save Draft button for new dispatches (no existing work order being edited)
  const showSaveDraft = true

  const handleNext = () => {
    // Check validation for steps 1, 2, and 3
    if ((step === 1 || step === 2 || step === 3) && !isStepValid) {
      setShowErrors(true)
      return // Don't proceed if validation fails
    }

    if (step < 4) {
      setStep(step + 1)
    } else {
      handleSubmit()
    }
  }

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1)
    }
  }

  // Get the modal title based on template type
  const modalTitle = useMemo(() => {
    if (templateRecipientType) {
      return DISPATCH_TEMPLATE_CONFIG[templateRecipientType].title
    }
    return 'Dispatch Details'
  }, [templateRecipientType])

  const renderStep = () => {
    switch (step) {
      case 1:
        // Use template contact info step if template type is specified
        if (templateRecipientType) {
          return (
            <TemplateContactInfoStep
              onUpdate={updateFormData}
              data={formData}
              project={fullProject}
              templateType={templateRecipientType}
              onValidationChange={handleValidationChange}
              showErrors={showErrors}
            />
          )
        }
        return (
          <ContactInfoStep
            onUpdate={updateFormData}
            data={formData}
            onValidationChange={handleValidationChange}
            showErrors={showErrors}
          />
        )
      case 2:
        return (
          <JobDetailsStep
            onUpdate={updateFormData}
            data={formData}
            project={project}
            onValidationChange={handleValidationChange}
            showErrors={showErrors}
            attachments={attachments}
            pendingAttachments={pendingAttachments}
            onPendingAttachmentsChange={setPendingAttachments}
            onAttachmentUpload={
              backendDraftId ? handleAttachmentUpload : undefined
            }
            onAttachmentDelete={
              backendDraftId ? handleAttachmentDelete : undefined
            }
          />
        )
      case 3:
        return (
          <PaymentInfoStep
            onUpdate={updateFormData}
            data={formData}
            onValidationChange={handleValidationChange}
            showErrors={showErrors}
          />
        )
      case 4:
        return (
          <WorkOrderConfirmation
            data={formData}
            attachments={attachments}
            pendingAttachments={pendingAttachments}
          />
        )
      default:
        return null
    }
  }

  return (
    <DashboardAddModal
      onClose={onClose}
      onSubmit={handleSubmit}
      onNext={handleNext}
      onBack={handleBack}
      title={modalTitle}
      projectId={dispatchLabel}
      poNumber={project?.po_number}
      step={step}
      totalSteps={4}
      isSubmitting={isSubmitting}
      onSaveDraft={handleSaveDraft}
      showSaveDraft={showSaveDraft}
      isSavingDraft={isSavingDraft}
      onClearDraft={clearDraft}
      submitError={submitError}
      onDismissSubmitError={() => setSubmitError(null)}
      autoSaveStatus={autoSaveStatus}
      autoSaveLastSaved={autoSaveLastSaved}
      sendConfirmTitle="Send Dispatch"
      sendConfirmMessage="Are you certain you want to send this Dispatch? Once sent, this Dispatch cannot be called back or modified."
      cancelConfirmTitle="Delete Dispatch"
      cancelConfirmMessage="Are you certain you want to permanently delete this Dispatch? It will no longer be available or accessible to you."
    >
      {renderStep()}
    </DashboardAddModal>
  )
}

const styles = StyleSheet.create({
  modal: {
    width: '100%',
    maxWidth: '800px',
    backgroundColor: 'white',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
  },
})

export default AddWorkOrderModal
